// src/array.ts
function isDefined(param) {
  return param !== null && param !== void 0;
}
Array.prototype.unique = function() {
  return [...new Set(this)];
};
Array.prototype.uniqueByKey = function(key) {
  const result = [];
  const group = this.groupBy((item) => item[key]);
  Object.keys(group).forEach((gKey) => {
    result.push(group[gKey].first);
  });
  return result;
};
Array.prototype.compact = function() {
  return this.filter(isDefined);
};
Array.prototype.sortBy = function(f) {
  return [...this].sort((a, b) => {
    const av = f(a);
    const bv = f(b);
    if (!isDefined(av) || !isDefined(bv)) {
      return !av && !bv ? 0 : !av ? 1 : -1;
    } else if (typeof av === "number" && typeof bv === "number") {
      return av - bv;
    } else if (typeof av === "string" && typeof bv === "string") {
      return av.localeCompare(bv);
    } else if (av instanceof Date && bv instanceof Date) {
      return av.getTime() - bv.getTime();
    } else {
      throw new Error(`Unsupported sortBy values ${av}, ${bv}`);
    }
  });
};
Array.prototype.sortByKey = function(key) {
  return this.sortBy((el) => el[key]);
};
Array.prototype.remove = function(element) {
  const index = this.indexOf(element);
  if (index > -1) {
    this.splice(index, 1);
  }
};
Array.prototype.partition = function(f) {
  const [trueElements, falseElements] = [[], []];
  this.forEach((el) => (f(el) ? trueElements : falseElements).push(el));
  return [trueElements, falseElements];
};
Array.prototype.each = function(f) {
  this.forEach(f);
  return this;
};
Array.prototype.asyncFilter = async function(predicate) {
  const results = await this.asyncMap(predicate);
  return this.filter((_v, index) => results[index]);
};
Array.prototype.asyncMap = async function(f) {
  return Promise.all(this.map(f));
};
Array.prototype.asyncForEach = async function(f) {
  return Promise.all(this.map(f)).then(() => {
  });
};
function sum(numbers) {
  return numbers.reduce((result, number) => result + (number ?? 0), 0);
}
Array.prototype.sum = function(f) {
  const promisesOrNumbers = f ? this.map(f) : this;
  if (promisesOrNumbers.some((v) => v instanceof Promise)) {
    return Promise.all(promisesOrNumbers).then(sum);
  } else {
    return sum(promisesOrNumbers);
  }
};
function sumOrUndefined(numbers) {
  return numbers.every((n) => n === void 0) ? void 0 : numbers.reduce((result, number) => result + (number ?? 0), 0);
}
Array.prototype.sumOrUndefined = function(f) {
  const promisesOrNumbers = f ? this.map(f) : this;
  if (promisesOrNumbers.first instanceof Promise) {
    return Promise.all(promisesOrNumbers).then(sumOrUndefined);
  } else {
    return sumOrUndefined(promisesOrNumbers);
  }
};
Array.prototype.groupBy = function(fn, valueFn) {
  const result = {};
  this.forEach((o) => {
    const group = fn(o);
    result[group] ??= [];
    result[group].push(valueFn ? valueFn(o) : o);
  });
  return result;
};
Array.prototype.groupByObject = function(fn, valueFn) {
  const result = /* @__PURE__ */ new Map();
  this.forEach((o) => {
    const key = fn(o);
    let group = result.get(key);
    if (group === void 0) {
      group = [];
      result.set(key, group);
    }
    group.push(valueFn ? valueFn(o) : o);
  });
  return [...result.entries()];
};
Array.prototype.batched = function(n) {
  const result = [];
  for (let i = 0; i < this.length; i += n) {
    result.push(this.slice(i, i + n));
  }
  return result;
};
Array.prototype.keyBy = function(fnOrKey, valueFn) {
  const result = {};
  const fn = typeof fnOrKey === "function" ? fnOrKey : (x) => x[fnOrKey];
  this.forEach((o) => {
    const group = fn(o);
    if (result[group] !== void 0) {
      throw new Error(`${String(group)} already had a value assigned`);
    }
    result[group] = valueFn ? valueFn(o) : o;
  });
  return result;
};
Array.prototype.keyByObject = function(fn, valueFn) {
  const result = /* @__PURE__ */ new Map();
  this.forEach((o) => {
    const group = fn(o);
    if (result.has(group)) {
      throw new Error(`${String(group)} already had a value assigned`);
    }
    result.set(group, valueFn ? valueFn(o) : o);
  });
  return result;
};
Array.prototype.findBefore = function(el) {
  const index = this.first === el ? -1 : this.indexOf(el);
  return index === -1 ? void 0 : this[index - 1];
};
Array.prototype.findAfter = function(el) {
  const index = this.last === el ? -1 : this.indexOf(el);
  return index === -1 ? void 0 : this[index + 1];
};
Array.prototype.findAndRemove = function(predicate) {
  const val = this.find(predicate);
  if (!val) {
    return void 0;
  }
  this.remove(val);
  return val;
};
Array.prototype.sentenceJoin = function(opts) {
  const { word = "and", separator = ", " } = opts ?? {};
  if (this.length > 2) {
    return `${this.slice(0, -1).join(separator)} ${word} ${this.last}`;
  } else if (this.length === 2) {
    return `${this.first} ${word} ${this.last}`;
  } else {
    return `${this.first ?? ""}`;
  }
};
Array.prototype.toObject = function() {
  return Object.fromEntries(this);
};
Array.prototype.mapToObject = function(f) {
  return this.map(f).toObject();
};
Array.prototype.max = function(f) {
  const values = f ? this.map(f) : this;
  const result = Math.max(...values);
  return values.first instanceof Date ? new Date(result) : result;
};
Array.prototype.min = function(f) {
  const values = f ? this.map(f) : this;
  const result = Math.min(...values);
  return values.first instanceof Date ? new Date(result) : result;
};
Array.prototype.count = function(f) {
  return this.reduce((count, ...args) => f(...args) ? count + 1 : count, 0);
};
Object.defineProperty(Array.prototype, "isEmpty", {
  enumerable: false,
  get: function() {
    return this.length === 0;
  }
});
Object.defineProperty(Array.prototype, "nonEmpty", {
  enumerable: false,
  get: function() {
    return this.length > 0;
  }
});
Object.defineProperty(Array.prototype, "first", {
  enumerable: false,
  get: function() {
    return this[0];
  }
});
Object.defineProperty(Array.prototype, "last", {
  enumerable: false,
  get: function() {
    return this.isEmpty ? void 0 : this[this.length - 1];
  }
});

// src/object.ts
var allowOverwritingPrototypeExtension = {
  configurable: true,
  enumerable: false,
  writable: true
};
Object.defineProperty(Object.prototype, "toEntries", {
  value: function() {
    return Object.entries(this);
  },
  ...allowOverwritingPrototypeExtension
});
Object.defineProperty(Object.prototype, "toValues", {
  value: function() {
    return Object.values(this);
  },
  ...allowOverwritingPrototypeExtension
});
Object.defineProperty(Object.prototype, "toKeys", {
  value: function() {
    return Object.keys(this);
  },
  ...allowOverwritingPrototypeExtension
});
//# sourceMappingURL=index.mjs.map